/**
 * Accessible Mega Menu Navigation
 * Handles click/tap interactions, keyboard navigation, and ARIA states
 */

class FundraisingNavigation {
  constructor() {
    this.navigation = document.querySelector('.uom-fv-navigation');
    this.toggleButtons = document.querySelectorAll('.uom-fv-nav-toggle');
    this.submenus = document.querySelectorAll('.uom-fv-navigation__submenu');
    
    if (!this.navigation || this.toggleButtons.length === 0) return;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.setInitialStates();
    
    setTimeout(() => {
      this.closeAllMenus();
    }, 100);
  }

  setInitialStates() {
    this.toggleButtons.forEach((button) => {
      const submenuId = button.getAttribute('aria-controls');
      const submenu = document.getElementById(submenuId);
      
      if (submenu) {
        button.setAttribute('aria-expanded', 'false');
        button.classList.remove('is-expanded');
        submenu.setAttribute('aria-hidden', 'true');
        submenu.classList.remove('is-expanded');
        
        const icon = button.querySelector('.uom-fv-nav-icon');
        if (icon) {
          icon.style.transform = 'rotate(0deg)';
        }
      }
    });
  }

  bindEvents() {
    this.toggleButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleToggle(button);
      });

      button.addEventListener('keydown', (e) => {
        this.handleKeydown(e, button);
      });
    });

    document.addEventListener('click', (e) => {
      if (!this.navigation.contains(e.target)) {
        this.closeAllMenus();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllMenus();
        const expandedButton = document.querySelector('.uom-fv-nav-toggle[aria-expanded="true"]');
        if (expandedButton) {
          expandedButton.focus();
        }
      }
    });

    this.submenus.forEach(submenu => {
      const links = submenu.querySelectorAll('a');
      links.forEach((link, index) => {
        link.addEventListener('keydown', (e) => {
          this.handleSubmenuKeydown(e, links, index);
        });
      });
    });
  }

  handleToggle(button) {
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const submenuId = button.getAttribute('aria-controls');
    const submenu = document.getElementById(submenuId);

    if (!submenu) return;

    if (isExpanded) {
      this.closeMenu(button, submenu);
    } else {
      this.closeAllMenus();
      this.openMenu(button, submenu);
    }
  }

  openMenu(button, submenu) {
    button.setAttribute('aria-expanded', 'true');
    button.classList.add('is-expanded');
    submenu.setAttribute('aria-hidden', 'false');
    submenu.classList.add('is-expanded');

    const icon = button.querySelector('.uom-fv-nav-icon');
    if (icon) {
      icon.style.transform = 'rotate(180deg)';
    }

    const firstLink = submenu.querySelector('a');
    if (firstLink) {
      setTimeout(() => {
        firstLink.focus();
      }, 100);
    }
  }

  closeMenu(button, submenu) {
    button.setAttribute('aria-expanded', 'false');
    button.classList.remove('is-expanded');
    submenu.setAttribute('aria-hidden', 'true');
    submenu.classList.remove('is-expanded');

    const icon = button.querySelector('.uom-fv-nav-icon');
    if (icon) {
      icon.style.transform = 'rotate(0deg)';
    }
  }

  closeAllMenus() {
    this.toggleButtons.forEach(button => {
      const submenuId = button.getAttribute('aria-controls');
      const submenu = document.getElementById(submenuId);
      if (submenu) {
        this.closeMenu(button, submenu);
      }
    });
  }

  handleKeydown(e, button) {
    const submenuId = button.getAttribute('aria-controls');
    const submenu = document.getElementById(submenuId);

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.handleToggle(button);
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (button.getAttribute('aria-expanded') === 'false') {
          this.handleToggle(button);
        } else if (submenu) {
          const firstLink = submenu.querySelector('a');
          if (firstLink) firstLink.focus();
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (submenu && button.getAttribute('aria-expanded') === 'true') {
          const links = submenu.querySelectorAll('a');
          const lastLink = links[links.length - 1];
          if (lastLink) lastLink.focus();
        }
        break;

      case 'ArrowLeft':
        e.preventDefault();
        this.focusPreviousButton(button);
        break;

      case 'ArrowRight':
        e.preventDefault();
        this.focusNextButton(button);
        break;
    }
  }

  handleSubmenuKeydown(e, links, currentIndex) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
        links[nextIndex].focus();
        break;

      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
        links[prevIndex].focus();
        break;

      case 'Tab':
        if (e.shiftKey && currentIndex === 0) {
          const expandedButton = document.querySelector('.uom-fv-nav-toggle[aria-expanded="true"]');
          if (expandedButton) {
            e.preventDefault();
            expandedButton.focus();
          }
        } else if (!e.shiftKey && currentIndex === links.length - 1) {
          this.closeAllMenus();
        }
        break;
    }
  }

  focusNextButton(currentButton) {
    const buttons = Array.from(this.toggleButtons);
    const currentIndex = buttons.indexOf(currentButton);
    const nextIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
    buttons[nextIndex].focus();
  }

  focusPreviousButton(currentButton) {
    const buttons = Array.from(this.toggleButtons);
    const currentIndex = buttons.indexOf(currentButton);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
    buttons[prevIndex].focus();
  }
}

// Global instance for debugging
let fundraisingNav = null;

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  fundraisingNav = new FundraisingNavigation();
});

// Also initialize if script loads after DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    fundraisingNav = new FundraisingNavigation();
  });
} else {
  fundraisingNav = new FundraisingNavigation();
}

// Global debug function
window.resetFundraisingNav = function() {
  if (fundraisingNav) {
    fundraisingNav.closeAllMenus();
    fundraisingNav.setInitialStates();
  }
};